import type { GroupRoundEnriched, Member } from '@/lib/db/types';
import { db } from '@/lib/db';
import { DEFAULT_DOTS } from '@/lib/games/formats/dots';
import { sixesPairings } from '@/lib/games/formats/sixes';
import { WolfHolePanel } from './WolfHolePanel';
import { catalogLabel } from '@/lib/games/game-catalog';
import { mergeWolfConfig } from '@/lib/games/formats/wolf';
import type { DotsHoleState, WolfHoleState, WolfRoundConfig } from '@/lib/games/engine/types';

interface Props {
  round: GroupRoundEnriched;
  member: Member;
  activeGameIds: string[];
  currentHole: number;
  onConfigUpdated: () => void;
}

export function ActiveRoundGamePanels({ round, member, activeGameIds, currentHole, onConfigUpdated }: Props) {
  const holeIx = Math.max(0, Math.min(17, currentHole));
  const players = round.players || [];
  const isOrganizer = round.organizer_id === member.id;

  const updateGameConfig = async (formatId: string, patch: Record<string, unknown>) => {
    const game = round.games?.find((g) => g.format_id === formatId && g.enabled);
    if (!game) return;
    await db.groupRounds.updateGameConfig(round.id, formatId, {
      ...(game.config as Record<string, unknown>),
      ...patch,
    });
    onConfigUpdated();
  };

  const panels = activeGameIds.filter((id) =>
    ['wolf', 'lone-wolf', 'dots', 'scramble', 'shamble', 'sixes'].includes(id),
  );

  if (!panels.length) return null;

  return (
    <div className="space-y-3">
      <div className="text-[10px] font-extrabold tracking-wide text-gold-deep">GAME CONTROLS · HOLE {holeIx + 1}</div>
      {panels.map((formatId) => {
        const game = round.games?.find((g) => g.format_id === formatId);
        const config = (game?.config || {}) as Record<string, unknown>;
        const holeState = (config.holeState || {}) as Record<number, WolfHoleState | DotsHoleState>;

        if (formatId === 'wolf' || formatId === 'lone-wolf') {
          const wolfConfig = mergeWolfConfig(config.wolfConfig as WolfRoundConfig | undefined);
          return (
            <WolfHolePanel
              key={formatId}
              round={round}
              member={member}
              holeIx={holeIx}
              formatId={formatId}
              holeState={holeState as Record<number, WolfHoleState>}
              wolfConfig={wolfConfig}
              onUpdate={(next) => void updateGameConfig(formatId, { holeState: next })}
            />
          );
        }

        if (formatId === 'dots') {
          const hs = holeState[holeIx + 1] as DotsHoleState | undefined;
          const manual = hs?.manual || {};
          const manualDots = DEFAULT_DOTS.filter((d) => !d.auto);
          return (
            <div key={formatId} className="rounded-xl border border-line bg-paper p-3">
              <div className="mb-2 text-xs font-bold text-navy">Dots / Junk</div>
              <div className="space-y-2">
                {players.map((p) => (
                  <div key={p.player_key}>
                    <div className="mb-1 text-[10px] font-bold text-mute">{p.display_name}</div>
                    <div className="flex flex-wrap gap-1">
                      {manualDots.map((d) => {
                        const on = (manual[p.player_key] || []).includes(d.id);
                        return (
                          <button
                            key={d.id}
                            type="button"
                            disabled={!isOrganizer}
                            onClick={() => {
                              const cur = manual[p.player_key] || [];
                              const next = on ? cur.filter((x) => x !== d.id) : [...cur, d.id];
                              void updateGameConfig('dots', {
                                holeState: {
                                  ...holeState,
                                  [holeIx + 1]: {
                                    manual: { ...manual, [p.player_key]: next },
                                  },
                                },
                              });
                            }}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${on ? 'bg-navy text-cream' : 'border border-line text-ink-soft'}`}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (formatId === 'sixes') {
          const [teamA, teamB] = sixesPairings(holeIx);
          const namesA = teamA.map((i) => players[i]?.display_name).filter(Boolean).join(' & ');
          const namesB = teamB.map((i) => players[i]?.display_name).filter(Boolean).join(' & ');
          return (
            <div key={formatId} className="rounded-xl border border-line bg-paper p-3">
              <div className="mb-1 text-xs font-bold text-navy">Sixes · this segment</div>
              <div className="text-[11px] text-ink-soft">{namesA} vs {namesB}</div>
            </div>
          );
        }

        if (formatId === 'scramble' || formatId === 'shamble') {
          const teams = round.config?.teams as string[][] | undefined;
          const teamScores = (config.teamScores || {}) as Record<string, (number | null)[]>;
          if (!teams || teams.length < 2) {
            return (
              <div key={formatId} className="rounded-xl border border-dashed border-line p-3 text-xs text-mute">
                Set teams for {catalogLabel(formatId)}
              </div>
            );
          }
          return (
            <div key={formatId} className="rounded-xl border border-line bg-paper p-3">
              <div className="mb-2 text-xs font-bold text-navy">{catalogLabel(formatId)} team scores</div>
              {[0, 1].map((ti) => (
                <div key={ti} className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold">Team {ti === 0 ? 'A' : 'B'}</span>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    disabled={!isOrganizer}
                    value={teamScores[String(ti)]?.[holeIx] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value === '' ? null : Number(e.target.value);
                      const row = [...(teamScores[String(ti)] || Array(18).fill(null))];
                      row[holeIx] = v;
                      void updateGameConfig(formatId, {
                        teamScores: { ...teamScores, [String(ti)]: row },
                      });
                    }}
                    className="w-14 rounded-lg border border-line px-2 py-1 text-center text-sm"
                  />
                </div>
              ))}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

/** First hole with incomplete scores — used as default hole index for game panels */
export function inferCurrentHole(scoresByKey: Record<string, (number | null)[]>, playerKeys: string[]): number {
  for (let h = 0; h < 18; h++) {
    const any = playerKeys.some((k) => scoresByKey[k]?.[h] != null);
    const all = playerKeys.every((k) => scoresByKey[k]?.[h] != null);
    if (any && !all) return h;
    if (!any) return h;
  }
  return 17;
}
